import React from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Progress } from '../ui/progress';
import { Users, Gift, UserPlus } from 'lucide-react';
import { motion } from 'framer-motion';

export interface TeamAdoptionV2Props {
	companyName: string;
	adopted: number;
	total: number;
	invitesSent: number;
	incentiveAmount?: number;
	onInvite?: () => void;
	translate: (key: string) => string;
}

const TeamAdoptionV2: React.FC<TeamAdoptionV2Props> = ({
	companyName,
	adopted,
	total,
	invitesSent,
	incentiveAmount = 10,
	onInvite,
	translate
}) => {
	const percentage = total > 0 ? Math.round((adopted / total) * 100) : 0;
	const remaining = total - adopted;

	return (
		<motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }}>
			<Card className="border-none shadow-xs overflow-hidden">
				<CardContent className="p-0">
					<div className="grid grid-cols-1 lg:grid-cols-2">
						{/* Left: Team adoption stats */}
						<div className="p-6 lg:border-r border-gray-200">
							<div className="flex items-center gap-2 mb-3">
								<Users className="w-5 h-5 text-primary" />
								<h3 className="font-semibold">
									{translate('benefit.team.adoptionIn')} {companyName}
								</h3>
							</div>
							<div className="flex items-end gap-3 mb-3">
								<span className="text-3xl font-bold text-primary">{percentage}%</span>
								<span className="text-sm text-gray-500 mb-1">
									{translate('benefit.team.colleaguesInPlan')}
								</span>
							</div>
							<Progress value={percentage} className="h-2 mb-3 [&>div]:bg-primary" />
							<p className="text-xs text-gray-500">
								{translate('benefit.team.stats')
									.replace('{adopted}', String(adopted))
									.replace('{total}', String(total))
									.replace('{remaining}', String(remaining))}
							</p>
						</div>

						{/* Right: Referral CTA */}
						<div className="p-6 bg-secondary/5">
							<div className="flex items-center gap-2 mb-3">
								<Gift className="w-5 h-5 text-secondary" />
								<h3 className="font-semibold">{translate('benefit.team.inviteAndWin')}</h3>
							</div>
							<p className="text-sm text-gray-500 mb-4">
								{translate('benefit.team.referralDescription').replace(
									'{amount}',
									String(remaining * incentiveAmount)
								)}
							</p>
							<div className="flex items-center gap-3">
								<Button
									onClick={onInvite}
									className="bg-secondary hover:bg-secondary/90 text-white rounded-full gap-2"
								>
									<UserPlus className="w-4 h-4" />
									{translate('benefit.team.inviteColleague')}
								</Button>
								<span className="text-xs text-gray-500">
									{(invitesSent !== 1
										? translate('benefit.team.youInvitedPlural')
										: translate('benefit.team.youInvited')
									).replace('{count}', String(invitesSent))}
								</span>
							</div>
						</div>
					</div>
				</CardContent>
			</Card>
		</motion.div>
	);
};

export default TeamAdoptionV2;
